import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Link,
  FormControl,
  MenuItem,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as FayeIcon } from "../icons/FayeBrand.svg";
import AuthService from "services/auth";
import { FayeAppBar } from "components/layout/NavigationLayout";
import { MEMBERSHIP_OPTIONS } from "components/signup/types";
import { MembershipOptionCard } from "components/signup/components/MembershipOptionCard";
import Select from "components/common/Select";
import { useSubmitReactivation } from "services/member";
import { SubmitReactivationRequest } from "protogen/member_service_pb";
import AddReviewDialog from "components/review/AddReviewDialog";
import { CurrentUserContext } from "components/context/RequireAuth";

type AdvisorPreference = "keep" | "change" | "no-preference";

const Logo = () => {
  return (
    <div>
      <FayeIcon
        style={{
          width: "80px",
          height: "48px",
          marginBottom: "22px",
        }}
      />
    </div>
  );
};

export default () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = AuthService.getCurrentUser();
  const [reviewOpen, setReviewOpen] = useState(
    () => searchParams.get("review") === "true",
  );
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [advisorPreference, setAdvisorPreference] =
    useState<AdvisorPreference>("no-preference");

  const { request: submitReactivation, loading } = useSubmitReactivation(() => {
    setSubmitted(true);
  });

  useEffect(() => {
    if (searchParams.get("review") === "true") {
      searchParams.delete("review");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const reviewDialog = currentUser ? (
    <CurrentUserContext.Provider value={currentUser}>
      <AddReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} />
    </CurrentUserContext.Provider>
  ) : null;

  const handleLogout = async () => {
    await AuthService.logout();
    localStorage.clear();
    navigate("/");
  };

  const handleReactivate = () => {
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (selectedPlan === null) return;

    const membershipPlan = MEMBERSHIP_OPTIONS[selectedPlan].name;

    await submitReactivation(
      new SubmitReactivationRequest({
        membershipPlan,
        advisorPreference,
      }),
    );
  };

  if (submitted) {
    return (
      <>
        <FayeAppBar />
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "500px",
              padding: "0px 28px",
              alignSelf: "stretch",
            }}
          >
            <Box sx={{ width: "80px", height: "48px" }}>
              <Logo />
            </Box>
            <Typography
              variant="h2Serif"
              sx={{
                marginBottom: "16px",
                textAlign: "center",
              }}
            >
              Thank you!
            </Typography>
            <Typography
              variant="body"
              sx={{
                marginBottom: "32px",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              We've received your reactivation request. We'll get back to you
              shortly to complete the process.
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleLogout}
              sx={{ marginBottom: "24px" }}
            >
              Log out
            </Button>
            <Typography
              variant="bodySmall"
              sx={{
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              Have questions? Contact us at{" "}
              <Link
                href="mailto:support@findfaye.com"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                }}
              >
                support@findfaye.com
              </Link>
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  if (showForm) {
    return (
      <>
        <FayeAppBar />
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: "600px",
            }}
          >
            <Logo />
            <Typography
              variant="h2Serif"
              sx={{
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              Reactivate your account
            </Typography>
            <Typography
              variant="body"
              sx={{
                marginBottom: "32px",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              Choose your membership and preferences
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                width: "100%",
              }}
            >
              <Box>
                <Typography variant="bodyHeavy" sx={{ marginBottom: "12px" }}>
                  Select a plan
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {MEMBERSHIP_OPTIONS.filter((m) => !m.hidden).map(
                    (option, idx) => (
                      <Box
                        onClick={() => setSelectedPlan(idx)}
                        key={option.name}
                        sx={{ cursor: "pointer" }}
                      >
                        <MembershipOptionCard
                          option={option}
                          isSelected={selectedPlan === idx}
                        />
                      </Box>
                    ),
                  )}
                </Box>
              </Box>

              <FormControl fullWidth>
                <Select
                  value={advisorPreference}
                  label="Advisor preference"
                  variant="outlined"
                  onChange={(e) =>
                    setAdvisorPreference(e.target.value as AdvisorPreference)
                  }
                >
                  <MenuItem value="keep">Keep the same advisor</MenuItem>
                  <MenuItem value="change">
                    I'd like to try a new advisor
                  </MenuItem>
                  <MenuItem value="no-preference">No preference</MenuItem>
                </Select>
              </FormControl>

              <Button
                fullWidth
                onClick={handleSubmit}
                disabled={selectedPlan === null || loading}
                sx={{ marginTop: "8px" }}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>

              <Button
                variant="text"
                fullWidth
                onClick={() => setShowForm(false)}
              >
                Back
              </Button>
            </Box>

            <Typography
              variant="bodySmall"
              sx={{
                marginTop: "24px",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              Have questions? Contact us at{" "}
              <Link
                href="mailto:support@findfaye.com"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                }}
              >
                support@findfaye.com
              </Link>
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <FayeAppBar />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "500px",
            padding: "0px 28px",
            alignSelf: "stretch",
          }}
        >
          <Logo />
          <Typography
            variant="h2Serif"
            sx={{
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            Your account is no longer active
          </Typography>
          <Typography
            variant="body"
            sx={{
              marginBottom: "32px",
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            To start using Faye again, please reactivate below.
          </Typography>
          <Button
            fullWidth
            onClick={handleReactivate}
            sx={{ marginBottom: "16px" }}
          >
            Reactivate account
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleLogout}
            sx={{ marginBottom: "24px" }}
          >
            Log out
          </Button>
          <Typography
            variant="bodySmall"
            sx={{
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            Anything else we can help with? Contact us at{" "}
            <Link
              href="mailto:support@findfaye.com"
              sx={{
                color: "primary.main",
                textDecoration: "none",
              }}
            >
              support@findfaye.com
            </Link>
          </Typography>
        </Box>
      </Box>
      {reviewDialog}
    </>
  );
};
